import React, { useState } from 'react';
import { BookOpen, FileText, ListChecks, Scissors, ChevronDown } from 'lucide-react';

const isSheet = (name) => /\.(xlsx|xls|csv)$/i.test(name || '');
const isDoc = (name) => /\.(docx|pdf)$/i.test(name || '');

/** Which plain-English card the file's current kind corresponds to. */
export const sortChoice = (f) => {
  if (f.kind === 'informational') return 'info';
  if (f.kind === 'narrative') return 'narrative';
  if (f.kind === 'questionnaire') return 'questions';
  if (f.kind === 'single_document') {
    const secs = f.outline?.sections || [];
    return secs.length && secs.every((s) => s.treatment === 'matrix') ? 'questions' : 'split';
  }
  return 'other';
};

const CARDS = [
  { id: 'info', title: 'Information only', Icon: BookOpen, blurb: 'Background reading — kept for checking answers against, nothing is built from it.', cls: 'border-slate-300 bg-slate-50 text-slate-700' },
  { id: 'narrative', title: 'Narrative to write', Icon: FileText, blurb: 'Its headings become sections you write (or AI drafts) as a Word response.', cls: 'border-indigo-300 bg-indigo-50 text-indigo-800' },
  { id: 'questions', title: 'Questions to answer', Icon: ListChecks, blurb: 'Every row or requirement becomes a question with a matched boilerplate answer.', cls: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
  { id: 'split', title: 'Split it', Icon: Scissors, blurb: 'Mixed document — decide section by section what is narrative and what is a question list.', cls: 'border-amber-300 bg-amber-50 text-amber-900' },
];

const OTHER = [['main_rfp', 'Main RFP document (instructions, dates, format)'], ['form', 'Client form to complete'], ['attachment', 'Attachment / acknowledge and return']];

/** One row of plain-English cards per uploaded file; translates the choice into the parser kinds/treatments behind the scenes. */
export const SortCards = ({ f, onOverride, disabled }) => {
  const [more, setMore] = useState(false);
  const cur = sortChoice(f);
  const available = (id) => {
    if (id === 'info') return true;
    if (id === 'narrative') return isDoc(f.filename) || f.kind === 'narrative';
    if (id === 'questions') return isSheet(f.filename) || !!f.outline || f.kind === 'questionnaire';
    if (id === 'split') return !!f.outline || f.kind === 'single_document';
    return false;
  };
  const choose = (id) => {
    if (disabled || id === cur) return;
    if (id === 'info') return onOverride(f.id, { kind: 'informational' });
    if (id === 'narrative') return onOverride(f.id, { kind: 'narrative' });
    if (id === 'questions') {
      if (isSheet(f.filename) || !f.outline) return onOverride(f.id, { kind: 'questionnaire' });
      return onOverride(f.id, { kind: 'single_document', treatments: Object.fromEntries((f.outline.sections || []).map((s) => [s.id, 'matrix'])) });
    }
    if (id === 'split') {
      const secs = f.outline?.sections || [];
      const restored = Object.fromEntries(secs.map((s) => [s.id, s.treatment_ai || s.treatment_heuristic || s.treatment]));
      const allMatrix = secs.length && Object.values(restored).every((t) => t === 'matrix');
      return onOverride(f.id, { kind: 'single_document', ...(secs.length && !allMatrix ? { treatments: restored } : {}) });
    }
  };
  return (
    <div className="space-y-1" data-testid={`rfp-sort-cards-${f.id}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
        {CARDS.map(({ id, title, Icon, blurb, cls }) => {
          const on = cur === id; const ok = available(id);
          return (
            <button key={id} type="button" disabled={disabled || !ok || f.pending} onClick={() => choose(id)}
              title={ok ? blurb : `Not available for this file type`}
              className={`text-left rounded-md border px-2.5 py-1.5 transition-colors ${on ? `${cls} border-2 shadow-sm` : 'border-slate-200 bg-white text-slate-600 hover:border-[#4ABFCF]'} disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`rfp-sort-card-${f.id}-${id}`} aria-pressed={on}>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold"><Icon className="w-3.5 h-3.5" />{title}{on && <span className="ml-auto text-[9px] font-normal opacity-70">chosen</span>}</span>
              <span className="block text-[10px] opacity-80 leading-snug mt-0.5">{blurb}</span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-slate-400">
        {cur === 'other' && <span className="text-slate-600" data-testid={`rfp-sort-other-${f.id}`}>Treated as: <b>{OTHER.find(([v]) => v === f.kind)?.[1] || f.kind}</b></span>}
        <button type="button" className="inline-flex items-center gap-0.5 hover:text-[#0F2040]" onClick={() => setMore((v) => !v)} data-testid={`rfp-sort-more-${f.id}`}><ChevronDown className={`w-3 h-3 transition-transform ${more ? 'rotate-180' : ''}`} />something else</button>
        {more && (
          <select value={cur === 'other' ? f.kind : ''} disabled={disabled} onChange={(e) => e.target.value && onOverride(f.id, { kind: e.target.value })} className="h-6 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-700" data-testid={`rfp-sort-other-select-${f.id}`}>
            <option value="">choose…</option>
            {OTHER.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
        {f.kind_guess && sortChoice({ ...f, kind: f.kind_guess }) !== cur && <span className="ml-auto">Blueprint's guess: {CARDS.find((c) => c.id === sortChoice({ ...f, kind: f.kind_guess }))?.title || f.kind_guess}</span>}
      </div>
    </div>
  );
};
